<template>
    <rkm-modal
        :title="readonly ? 'Dados do aprazamento' : 'Novo aprazamento'"
        extra-large
        ref="RkmModal"
    >
        <rkm-form
            v-if="model"
            :show-legend="readonly ? false : true"
            ref="RkmForm"
        >
            <template v-if="readonly">
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :value="local.dataAprazamento"
                            disabled
                            label="Data"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="cidadaoIdade"
                            disabled
                            label="Idade do paciente na data do aprazamento"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="8">
                        <rkm-form-input
                            :value="inputVacinaValue"
                            disabled
                            label="Vacina"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="local.doseNome"
                            disabled
                            label="Dose"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-form-textarea
                            label="Observação"
                            readonly
                            rows="4"
                            v-model="local.observacao"
                        />
                    </rkm-col>
                </rkm-row>
            </template>

            <template v-else>
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :min-date="others.moment(additionals.dataAtual).add(1, 'days').format('DD/MM/YYYY')"
                            :validations="validations.dataAprazamento"
                            label="Data"
                            v-model="local.dataAprazamento"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input
                            :value="cidadaoIdade"
                            disabled
                            label="Idade do paciente na data do aprazamento"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="8">
                        <rkm-form-input-autocomplete
                            :disabled="!local.dataAprazamento"
                            :repository="repositories.vacina"
                            :request-query-string-params="{origem: 'aprazamento'}"
                            :search-fields="fields.autocomplete"
                            :text-field="item => item.id ? `${autocompleteTextField(item)}` : item.nome"
                            :validations="validations.vacina"
                            @input="handleVacinaInput"
                            @select="handleVacinaSelect"
                            label="Vacina"
                            v-model="local.vacina"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!local.vacina"
                            :options="form.doses"
                            :validations="validations.doseId"
                            label="Dose"
                            show-default-option
                            text-field="nome"
                            v-model="local.doseId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-form-textarea
                            :validations="validations.observacao"
                            label="Observação"
                            rows="4"
                            v-model="local.observacao"
                        />
                    </rkm-col>
                </rkm-row>
            </template>
        </rkm-form>

        <template v-slot:footer="{ cancel }">
            <rkm-button @click="cancel">
                {{ readonly ? 'Fechar' : 'Cancelar' }}
            </rkm-button>

            <rkm-button
                v-if="!readonly"
                @click="include"
                color="primary"
            >
                incluir
            </rkm-button>
        </template>
    </rkm-modal>
</template>

<script>
// Enums.
import parametrizacaoDescricaoVacina from '@/enums/imunizacao/parametrizacaoDescricaoVacina'

// Mixins.
import { error, repository, stringCase } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    computed: {
        cidadaoIdade() {
            const dataNascimento = moment(this.model.cidadaoDataNascimento)

            const anos = moment(this.local.dataAprazamento).diff(dataNascimento, 'years', false)
            const meses = moment(this.local.dataAprazamento).diff(dataNascimento.add(anos, 'years'), 'months', false)
            const dias = moment(this.local.dataAprazamento).diff(dataNascimento.add(meses, 'months'), 'days', false)

            if (this.local.dataAprazamento) {
                return `${anos}a, ${meses}m e ${dias}d`
            }

            return null
        },
        fields() {
            const autocomplete = []

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    autocomplete.push({ key: 'sigla', searchable: true })
                    autocomplete.push({ key: 'abreviacao', searchable: true })
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;

                default:
                    autocomplete.push({ key: 'nome', searchable: true })
                    break;
            }

            return { autocomplete }
        },
        validations() {
            return {
                dataAprazamento: { required: true },
                vacina: { required: true },
                doseId: { required: true },
                observacao: { min: 3, max: 5000 },
            }
        },
        inputVacinaValue() {
            let value = null

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    value = this.local.vacina.abreviacao
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    value = this.local.vacina.sigla
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    value = this.local.vacina.siglaNome
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    value = this.local.vacina.siglaAbreviacao
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    value = this.local.vacina.abreviacaoNome
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    value = this.local.vacina.siglaAbreviacaoNome
                    break;

                default:
                    value = this.local.vacina.nome
                    break;
            }

            return value
        },
    },
    created() {
        this.repositories = {
            registroVacinacao: this.getConectaSusRepository('imunizacao.registroVacinacao'),
            fichaVacinacao: this.getConectaSusRepository('atencaoBasica.fichaVacinacao'),
            vacina: this.getConectaSusRepository('cadastro.vacina'),
        }
    },
    data: () => ({
        enums: { parametrizacaoDescricaoVacina },
        local: {
            index: null,
            dataAprazamento: null,
            vacina: null,
            doseId: null,
            doseNome: null,
            observacao: null,
        },
        others: { moment },
        readonly: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        open(item, readonly = false, index = null) {
            this.resetForm()

            this.readonly = readonly
            this.local.index = index

            if (item) {
                if (readonly === false) {
                    this.getDoses(item.vacinaId)
                }

                this.local.id = item.id
                this.local.dataAprazamento = moment(item.dataAprazamento, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                this.local.vacina = {
                    id: item.vacinaId,
                    nome: item.vacinaNome,
                    abreviacao: item.vacinaAbreviacao,
                    sigla: item.vacinaSigla,
                    siglaNome: `${item.vacinaSigla} - ${item.vacinaNome}`,
                    siglaAbreviacao: `${item.vacinaSigla} - ${item.vacinaAbreviacao}`,
                    abreviacaoNome: `${item.vacinaAbreviacao} - ${item.vacinaNome}`,
                    siglaAbreviacaoNome: `${item.vacinaSigla} - ${item.vacinaAbreviacao} -
                        ${item.vacinaNome}`,
                }
                this.local.doseId = item.doseId
                this.local.doseNome = item.doseNome
                this.local.observacao = item.observacao
            }

            this.$refs.RkmModal.show()
        },
        handleVacinaInput() {
            this.form.doses = null
            this.local.doseId = null
            this.local.doseNome = null
        },
        handleVacinaSelect(value) {
            this.getDoses(value.id)
        },
        getDoses(vacinaId) {
            const request = {
                origem: 'aprazamento',
                vacina_id: vacinaId,
            }

            this.setLoading()

            this.repositories.fichaVacinacao.getDoses(request)
                .then(response => {
                    this.form.doses = response.data
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        hasAplicacao() {

            return this.model.aprazamentos.find(r => r.id != this.local.id
                && r.vacinaId == this.local.vacina.id
                && r.doseId == this.local.doseId
            )
        },
        handleInclude() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid === false) return

                    if (this.hasAplicacao()) {

                        this.showAlert('Já existe um registro de aprazamento com a vacina e dose para o paciente.', 'Atenção')

                        return false
                    }

                    const dose = this.form.doses.find(dose => dose.id === this.local.doseId)

                    if (this.local.index !== null) {
                        Object.assign(this.model.aprazamentos[this.local.index], {
                            id: this.local.id,
                            dataAprazamento: moment(this.local.dataAprazamento).format('DD/MM/YYYY'),
                            vacinaId: this.local.vacina.id,
                            vacinaNome: this.local.vacina.nome,
                            vacinaAbreviacao: this.local.vacina.abreviacao,
                            vacinaSigla: this.local.vacina.sigla,
                            vacinaSiglaNome: `${this.local.vacina.sigla} - ${this.local.vacina.nome}`,
                            vacinaSiglaAbreviacao: `${this.local.vacina.sigla} - ${this.local.vacina.abreviacao}`,
                            vacinaAbreviacaoNome: `${this.local.vacina.abreviacao} - ${this.local.vacina.nome}`,
                            vacinaSiglaAbreviacaoNome: `${this.local.vacina.sigla} - ${this.local.vacina.abreviacao} -
                                ${this.local.vacina.nome}`,
                            doseId: dose.id,
                            doseNome: dose.nome,
                            observacao: this.local.observacao,
                        })
                    } else {
                        this.model.aprazamentos.push({
                            id: this.uniqueId('aprazamento' + new Date().getTime()),
                            dataAprazamento: moment(this.local.dataAprazamento).format('DD/MM/YYYY'),
                            vacinaId: this.local.vacina.id,
                            vacinaNome: this.local.vacina.nome,
                            vacinaAbreviacao: this.local.vacina.abreviacao,
                            vacinaSigla: this.local.vacina.sigla,
                            vacinaSiglaNome: `${this.local.vacina.sigla} - ${this.local.vacina.nome}`,
                            vacinaSiglaAbreviacao: `${this.local.vacina.sigla} - ${this.local.vacina.abreviacao}`,
                            vacinaAbreviacaoNome: `${this.local.vacina.abreviacao} - ${this.local.vacina.nome}`,
                            vacinaSiglaAbreviacaoNome: `${this.local.vacina.sigla} - ${this.local.vacina.abreviacao} -
                                ${this.local.vacina.nome}`,
                            doseId: dose.id,
                            doseNome: dose.nome,
                            observacao: this.local.observacao,
                        })
                    }

                    this.$refs.RkmModal.hide()
                })
        },
        include() {
            this.handleInclude()
        },
        resetForm() {
            this.local.id = null
            this.local.dataAprazamento = null
            this.local.vacina = null
            this.local.doseId = null
            this.local.doseNome = null
            this.local.observacao = null
        },
        autocompleteTextField(item) {

            let textField = null

            switch (this.additionals.parametrizacao.parametrizacaoDescricaoVacinaId) {
                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA:
                    textField = `${item.abreviacao}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA:
                    textField = `${item.sigla}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO:
                    textField = `${item.sigla} - ${item.nome}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA:
                    textField = `${item.sigla} - ${item.abreviacao}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.DESCRICAO_ABREVIADA_DESCRICAO:
                    textField = `${item.abreviacao} - ${item.nome}`
                    break;

                case this.enums.parametrizacaoDescricaoVacina.SIGLA_DESCRICAO_ABREVIADA_DESCRICAO:
                    textField = `${item.sigla} - ${item.abreviacao} - ${item.nome}`
                    break;

                default:
                    textField = `${item.nome}`
                    break;
            }

            return textField
        },
    },
    mixins: [ error, repository, stringCase ],
    props: {
        additionals: { type: Object, required: true },
        form: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        model: { type: Object, required: true },
    },
}
</script>